import { useCallback, useEffect, useRef, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { getWindowSafe } from '@change-corgi/core/window';

type Result = ModelHookResult<
	{
		prevLabel: string;
		nextLabel: string;
		canScrollLeft: boolean;
		canScrollRight: boolean;
		containerRef: React.RefObject<HTMLDivElement | null>;
	},
	{
		handlePrevious: () => void;
		handleNext: () => void;
	}
>;

export function useCarousel(): Result {
	const containerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const window = getWindowSafe();
	const { translate } = useI18n();

	const checkScrollability = useCallback(() => {
		if (!containerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
		setCanScrollLeft(scrollLeft > 0);
		setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
	}, []);

	useEffect(() => {
		if (!window) return () => {};
		const container = containerRef.current;
		if (!container) return () => {};

		checkScrollability();

		container.addEventListener('scroll', checkScrollability);
		window.addEventListener('resize', checkScrollability);

		return () => {
			container.removeEventListener('scroll', checkScrollability);
			window.removeEventListener('resize', checkScrollability);
		};
	}, [checkScrollability, window]);

	const scroll = useCallback((direction: 'left' | 'right') => {
		if (!containerRef.current) return;

		const container = containerRef.current;
		const scrollAmount = container.clientWidth;

		container.scrollBy({
			left: direction === 'left' ? -scrollAmount : scrollAmount,
			behavior: 'smooth',
		});
	}, []);

	const handlePrevious = useCallback(() => scroll('left'), [scroll]);
	const handleNext = useCallback(() => scroll('right'), [scroll]);

	return {
		data: {
			prevLabel: translate('corgi.components.scroll_container.navigation_btn.previous'),
			nextLabel: translate('corgi.components.scroll_container.navigation_btn.next'),
			canScrollLeft,
			canScrollRight,
			containerRef,
		},
		actions: {
			handlePrevious,
			handleNext,
		},
	};
}
